// Support access — a runner lets Paceroo support sign in as them for a while,
// and support's visits are recorded where the runner can see them.
// Wire shapes shared by the API (apps/api/src/support), the runner app
// (Profile → Support access, the in-session pill) and the admin console.
// Guard rails: design/architecture/notes/admin-console.md §5.

import type { AdminAuditEntry } from "./admin";

/** How long a runner can allow access for. */
export const SUPPORT_GRANT_HOURS = [1, 24, 72] as const;
export type SupportGrantHours = (typeof SUPPORT_GRANT_HOURS)[number];

/** 'read' unless the runner also allowed changes. */
export type SupportScope = "read" | "write";

/** Lengths support can pick for one visit (capped at the grant's expiry). */
export const SUPPORT_SESSION_MINUTES = [15, 30, 60] as const;
export type SupportSessionMinutes = (typeof SUPPORT_SESSION_MINUTES)[number];

export interface SupportGrant {
  id: string;
  grantedAt: string;
  expiresAt: string;
  scope: SupportScope;
  /** As granted (1, 24 or 72). */
  hours: number;
}

export type SupportHistoryItem =
  | { kind: "granted"; at: string; hours: number; scope: SupportScope }
  | { kind: "revoked"; at: string }
  | { kind: "visit"; at: string; endAt: string; mode: SupportScope; changes: number };

/** GET /api/runner/support-access */
export interface SupportAccessState {
  active: SupportGrant | null;
  /** Newest first. */
  history: SupportHistoryItem[];
}

/** POST /api/runner/support-access */
export interface SupportGrantRequest {
  hours: SupportGrantHours;
  /** Also let support make changes (default: read-only). */
  allowChanges?: boolean;
}

/** GET /api/support/session — only "active" while support is signed in as this runner. */
export type SupportSessionInfo =
  | { active: false }
  | {
      active: true;
      runnerName: string;
      runnerEmail: string;
      mode: SupportScope;
      startedAt: string;
      endsAt: string;
      reason: string;
      adminEmail: string;
    };

/** POST /api/auth/support/end */
export interface SupportSessionEnd {
  /** Where to send the admin back to (the console's runner page). */
  consoleUrl: string;
}

/** POST /api/admin/runners/:id/impersonation */
export interface ImpersonationRequest {
  reason: string;
  mode: SupportScope;
  minutes: SupportSessionMinutes;
}

export interface ImpersonationStart {
  /** Open in a new tab: signs the browser in as the runner on the app's host, once, within 60 s. */
  handoffUrl: string;
  endsAt: string;
  audit: AdminAuditEntry;
}

/** A visit as the console lists it on runner detail. */
export interface AdminSupportSession {
  id: string;
  adminEmail: string;
  mode: SupportScope;
  reason: string;
  startedAt: string;
  endsAt: string;
  endedAt: string | null;
  endReason: string | null;
  changes: number;
}
